import { createEvent } from "../api/calendarApi";
import { createBuyer } from "../api/buyersApi";
import {
  ensureJourneyDb,
  getById,
  getUserByEmail,
  insertRow,
  rowsFromIndex,
  updateRow,
  writeAudit,
} from "../db";
import { createMessage, createThread, createTransaction, createUser, nowIso, newId } from "../db/schema";
import { notifyUser } from "../api/messaging/helpers";
import {
  CONTACT_REASONS,
  INTEREST_LEVELS,
  PROSPECTS_EVENT,
  PROSPECTS_PREFIX,
  addDaysStamp,
  canManageProspects,
  canViewProspects,
  followDaysForInterest,
  interestFromReason,
  isFollowOverdue,
} from "./constants.js";

const KEYS = {
  rows: `${PROSPECTS_PREFIX}rows`,
  timeline: `${PROSPECTS_PREFIX}timeline`,
};

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
  emitProspectsChange();
}

export function emitProspectsChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(PROSPECTS_EVENT));
}

export function ensureProspects() {
  ensureJourneyDb();
  if (!Array.isArray(read(KEYS.rows, null))) write(KEYS.rows, []);
  if (!Array.isArray(read(KEYS.timeline, null))) write(KEYS.timeline, []);
  return true;
}

function assertView(session) {
  if (!canViewProspects(session)) throw new Error("Prospects are for signed-in brokers and agents.");
}

function assertManage(session) {
  assertView(session);
  if (!canManageProspects(session)) throw new Error("This desk may look, but not change prospect lists.");
}

function owns(session, row) {
  if (!row) return false;
  if (session.role === "admin") return true;
  if (session.role === "broker") {
    return row.owner_id === session.userId || row.brokerage_id === session.userId;
  }
  return row.owner_id === session.userId;
}

function allRows() {
  ensureProspects();
  return read(KEYS.rows, []);
}

function saveRows(rows) {
  write(KEYS.rows, rows);
}

function allTimeline() {
  ensureProspects();
  return read(KEYS.timeline, []);
}

function addTimeline(prospectId, kind, body) {
  const row = { id: newId(), prospect_id: prospectId, at: nowIso(), kind, body };
  write(KEYS.timeline, [...allTimeline(), row]);
  return row;
}

export function listProspectTimeline(prospectId) {
  return allTimeline()
    .filter((row) => row.prospect_id === prospectId)
    .sort((a, b) => String(a.at).localeCompare(String(b.at)));
}

export function listProspects(session) {
  assertView(session);
  return allRows()
    .filter((row) => owns(session, row))
    .map(withDerived)
    .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
}

export function getProspect(id, session) {
  assertView(session);
  const row = allRows().find((item) => item.id === id);
  if (!row || !owns(session, row)) return null;
  return withDerived(row);
}

export function findProspectByTransaction(transactionId, session) {
  if (!transactionId) return null;
  return listProspects(session).find((row) => row.transaction_id === transactionId) || null;
}

function withDerived(row) {
  return {
    ...row,
    overdue: isFollowOverdue(row),
    interest_label: INTEREST_LEVELS.find((item) => item.id === row.interest)?.label || row.interest,
    reason_label: CONTACT_REASONS.find((item) => item.id === row.reason)?.label || row.reason || "",
  };
}

export function listProspectMessages(prospect) {
  if (!prospect?.thread_id) return [];
  ensureJourneyDb();
  return rowsFromIndex("messages", "messagesByThreadId", prospect.thread_id).sort((a, b) =>
    String(a.timestamp).localeCompare(String(b.timestamp))
  );
}

export function prospectPortalPath(ownerId) {
  return `/connect/${encodeURIComponent(ownerId)}`;
}

export function prospectPortalUrl(ownerId) {
  if (typeof window === "undefined") return prospectPortalPath(ownerId);
  return `${window.location.origin}${prospectPortalPath(ownerId)}`;
}

function ownerRecord(ownerId) {
  ensureJourneyDb();
  const user = getById("users", ownerId);
  if (!user || (user.role_type !== "agent" && user.role_type !== "broker")) return null;
  return user;
}

function ensureBridge(session, draft) {
  ensureJourneyDb();
  const owner = ownerRecord(session.userId);
  if (!owner) throw new Error("Your desk could not be found.");
  const brokerageId = session.role === "broker" ? session.userId : session.brokerageId || owner.brokerage_id || null;
  let user = getUserByEmail(draft.email);
  if (user && user.role_type !== "prospect") {
    throw new Error("That email is already on a client File.");
  }
  if (!user) {
    user = insertRow(
      "users",
      createUser({
        role_type: "prospect",
        name: draft.name,
        email: draft.email,
        phone: draft.phone || "",
        status: "active",
        brokerage_id: brokerageId,
      })
    );
  }
  let transaction = rowsFromIndex("transactions", "transactionsByAgentId", session.userId).find(
    (row) => row.client_id === user.id && row.client_role === "prospect" && row.status === "open"
  );
  if (!transaction) {
    transaction = insertRow(
      "transactions",
      createTransaction({
        broker_id: brokerageId,
        agent_id: session.userId,
        client_id: user.id,
        client_role: "prospect",
        property_address: draft.property_of_interest ? `Prospect · ${draft.property_of_interest}` : "Prospect",
        status: "open",
      })
    );
  }
  let thread = rowsFromIndex("message_threads", "threadsByTransactionId", transaction.id)[0];
  if (!thread) {
    thread = insertRow(
      "message_threads",
      createThread({
        transaction_id: transaction.id,
        created_by_user_id: session.userId,
        status: "active",
      })
    );
  }
  return { user, transaction, thread, brokerageId };
}

export function createProspect(session, input) {
  assertManage(session);
  const name = String(input.name || "").trim();
  const email = String(input.email || "").trim().toLowerCase();
  if (!name || !email) throw new Error("Name and email are needed.");
  const existing = allRows().find((row) => row.owner_id === session.userId && row.email === email && row.status === "open");
  if (existing) throw new Error("That person is already on your prospect list.");
  const interest = input.interest || interestFromReason(input.reason);
  const draft = {
    name,
    email,
    phone: String(input.phone || "").trim(),
    preferred_contact: input.preferred_contact || "email",
    notes: String(input.notes || "").trim(),
    interest,
    reason: input.reason || "general",
    property_of_interest: String(input.property_of_interest || "").trim(),
  };
  const bridge = ensureBridge(session, draft);
  const now = nowIso();
  const row = {
    id: newId(),
    owner_id: session.userId,
    brokerage_id: bridge.brokerageId,
    user_id: bridge.user.id,
    transaction_id: bridge.transaction.id,
    thread_id: bridge.thread.id,
    ...draft,
    status: "open",
    converted_kind: null,
    converted_at: null,
    last_contact: now,
    next_contact: input.next_contact || addDaysStamp(followDaysForInterest(interest)),
    follow_status: "scheduled",
    follow_note: String(input.follow_note || "").trim(),
    source: "desk",
    created_at: now,
    updated_at: now,
  };
  saveRows([...allRows(), row]);
  addTimeline(row.id, "created", "Prospect added from the desk.");
  scheduleCalendar(session, row);
  writeAudit({
    user_id: session.userId,
    action_type: "prospect.create",
    transaction_id: row.transaction_id,
    metadata: { prospect_id: row.id },
  });
  return withDerived(row);
}

export function updateProspect(session, id, patch) {
  assertManage(session);
  const rows = allRows();
  const index = rows.findIndex((row) => row.id === id);
  if (index === -1 || !owns(session, rows[index])) throw new Error("Prospect not found.");
  if (rows[index].status === "converted" && patch.status !== "open") {
    throw new Error("This prospect already has a File.");
  }
  const next = { ...rows[index], ...patch, id, updated_at: nowIso() };
  rows[index] = next;
  saveRows(rows);
  addTimeline(id, "update", "Prospect details were updated.");
  return withDerived(next);
}

export function setFollowUp(session, id, { next_contact, follow_note, follow_status }) {
  assertManage(session);
  const current = getProspect(id, session);
  if (!current) throw new Error("Prospect not found.");
  const next = updateProspect(session, id, {
    next_contact: next_contact || current.next_contact,
    follow_note: follow_note != null ? follow_note : current.follow_note,
    follow_status: follow_status || current.follow_status || "scheduled",
  });
  addTimeline(id, "followup", follow_status === "done" ? "Follow-up marked done." : "Follow-up was set.");
  if (follow_status !== "done") scheduleCalendar(session, next);
  return next;
}

function scheduleCalendar(session, row) {
  if (!row.next_contact) return;
  createEvent({
    agentId: session.userId,
    buyerId: row.user_id,
    title: `Follow up · ${row.name}`,
    date: row.next_contact,
    time: "09:00",
    type: "followup",
    contactName: row.name,
    notes: row.follow_note || "Prospect follow-up",
    description: row.notes || "",
  }).catch(() => {});
}

export function sendProspectNote(session, id, text) {
  assertManage(session);
  const prospect = getProspect(id, session);
  if (!prospect) throw new Error("Prospect not found.");
  const body = String(text || "").trim();
  if (!body) throw new Error("Write a short note first.");
  ensureJourneyDb();
  insertRow(
    "messages",
    createMessage({
      thread_id: prospect.thread_id,
      sender_id: session.userId,
      receiver_id: prospect.user_id,
      message_text: body,
    })
  );
  const rows = allRows().map((row) =>
    row.id === id ? { ...row, last_contact: nowIso(), updated_at: nowIso() } : row
  );
  saveRows(rows);
  addTimeline(id, "message", body);
  return getProspect(id, session);
}

export function convertProspect(session, id, kind) {
  assertManage(session);
  const role = kind === "seller" ? "seller" : kind === "buyer" ? "buyer" : null;
  if (!role) throw new Error("Choose buyer or seller. Those journeys stay apart.");
  const prospect = getProspect(id, session);
  if (!prospect) throw new Error("Prospect not found.");
  if (prospect.status === "converted") throw new Error("This prospect already has a File.");

  const user = getById("users", prospect.user_id);
  if (user) {
    updateRow("users", user.id, { role_type: role, name: prospect.name, phone: prospect.phone || user.phone });
  }

  if (role === "buyer") {
    const buyers = JSON.parse(localStorage.getItem("dh_buyers") || "[]");
    if (!buyers.some((row) => row.id === prospect.user_id || row.email === prospect.email)) {
      createBuyer({
        id: prospect.user_id,
        name: prospect.name,
        email: prospect.email,
        phone: prospect.phone,
        agentId: session.userId,
        status: "active",
      });
    }
  } else {
    appendConvertedSeller(session, prospect);
  }

  if (prospect.transaction_id) {
    updateRow("transactions", prospect.transaction_id, {
      client_role: role,
      property_address: prospect.property_of_interest || `File · ${prospect.name}`,
    });
  }

  const rows = allRows().map((row) =>
    row.id === id
      ? {
          ...row,
          status: "converted",
          converted_kind: role,
          converted_at: nowIso(),
          follow_status: "done",
          notes: [row.notes, "Converted. Notes and timeline stay on this prospect card."]
            .filter(Boolean)
            .join("\n"),
          updated_at: nowIso(),
        }
      : row
  );
  saveRows(rows);
  addTimeline(id, "converted", `Moved into a ${role} File. Notes and history stay here.`);
  writeAudit({
    user_id: session.userId,
    action_type: "prospect.convert",
    transaction_id: prospect.transaction_id,
    metadata: { prospect_id: id, kind: role },
  });
  return getProspect(id, session);
}

function appendConvertedSeller(session, prospect) {
  let list = [];
  try {
    list = JSON.parse(localStorage.getItem("dh_sellers") || "[]");
  } catch {
    list = [];
  }
  if (list.some((row) => row.id === prospect.user_id || row.email === prospect.email)) return;
  list.push({
    id: prospect.user_id,
    name: prospect.name,
    email: prospect.email,
    phone: prospect.phone,
    agentId: session.userId,
    agentName: session.name || "",
    office: session.office_name || "",
    status: "active",
    notes: { forAgent: prospect.notes || "" },
  });
  localStorage.setItem("dh_sellers", JSON.stringify(list));
}

export function submitPortalMessage(form, deskId) {
  ensureProspects();
  const owner = ownerRecord(deskId);
  if (!owner) throw new Error("That desk could not be found.");
  const name = String(form.name || "").trim();
  const email = String(form.email || "").trim().toLowerCase();
  const message = String(form.message || "").trim();
  if (!name || !email || !message) throw new Error("Name, email, and a short note are needed.");
  const existingUser = getUserByEmail(email);
  if (existingUser && existingUser.role_type !== "prospect") {
    throw new Error("That email already has a client File. Please write through your agent’s usual door.");
  }

  const reason = form.reason || "general";
  const interest = interestFromReason(reason);
  const property = String(form.property_of_interest || "").trim();
  const meeting = String(form.meeting_time || "").trim();
  const phone = String(form.phone || "").trim();
  const brokerageId = owner.role_type === "broker" ? owner.id : owner.brokerage_id || null;

  let user = existingUser;
  if (!user) {
    user = insertRow(
      "users",
      createUser({
        role_type: "prospect",
        name,
        email,
        phone,
        status: "active",
        brokerage_id: brokerageId,
      })
    );
  }

  const agentId = owner.role_type === "broker" ? owner.id : owner.id;
  let transaction = rowsFromIndex("transactions", "transactionsByAgentId", agentId).find(
    (row) => row.client_id === user.id && row.client_role === "prospect" && row.status === "open"
  );
  if (!transaction) {
    transaction = insertRow(
      "transactions",
      createTransaction({
        broker_id: brokerageId,
        agent_id: agentId,
        client_id: user.id,
        client_role: "prospect",
        property_address: property ? `Prospect · ${property}` : "Prospect",
        status: "open",
      })
    );
  }
  let thread = rowsFromIndex("message_threads", "threadsByTransactionId", transaction.id)[0];
  if (!thread) {
    thread = insertRow(
      "message_threads",
      createThread({
        transaction_id: transaction.id,
        created_by_user_id: user.id,
        status: "active",
      })
    );
  }

  insertRow(
    "messages",
    createMessage({
      thread_id: thread.id,
      sender_id: user.id,
      receiver_id: agentId,
      message_text: composePortalNote(form, message, meeting),
    })
  );
  notifyUser({
    user_id: agentId,
    type: "message",
    payload: { thread_id: thread.id, transaction_id: transaction.id, prospect: true },
  });

  const rows = allRows();
  let row = rows.find((item) => item.owner_id === agentId && item.email === email && item.status === "open");
  const now = nowIso();
  const nextContact = meetingStamp(meeting) || addDaysStamp(followDaysForInterest(interest));
  if (!row) {
    row = {
      id: newId(),
      owner_id: agentId,
      brokerage_id: brokerageId,
      user_id: user.id,
      transaction_id: transaction.id,
      thread_id: thread.id,
      name,
      email,
      phone,
      preferred_contact: phone ? "either" : "email",
      notes: "",
      interest,
      reason,
      property_of_interest: property,
      status: "open",
      converted_kind: null,
      converted_at: null,
      last_contact: now,
      next_contact: nextContact,
      follow_status: "scheduled",
      follow_note: meeting ? `Asked to meet: ${meeting}` : "",
      source: "portal",
      created_at: now,
      updated_at: now,
    };
    saveRows([...rows, row]);
    addTimeline(row.id, "created", "Wrote in through the public door. A prospect card was opened.");
  } else {
    const next = {
      ...row,
      name,
      phone: phone || row.phone,
      last_contact: now,
      interest,
      reason,
      property_of_interest: property || row.property_of_interest,
      next_contact: nextContact,
      follow_status: "scheduled",
      updated_at: now,
    };
    saveRows(rows.map((item) => (item.id === row.id ? next : item)));
    row = next;
  }
  addTimeline(row.id, "message", message);
  addTimeline(row.id, "followup", `Follow-up set for ${nextContact}.`);
  createEvent({
    agentId,
    buyerId: user.id,
    title: `Follow up · ${name}`,
    date: nextContact,
    time: "09:00",
    type: "followup",
    contactName: name,
    notes: message.slice(0, 160),
  }).catch(() => {});
  return { ok: true };
}

function composePortalNote(form, message, meeting) {
  const reason = CONTACT_REASONS.find((item) => item.id === form.reason)?.label || "Note";
  const bits = [message, form.property_of_interest ? `Property of interest: ${form.property_of_interest}` : "", meeting ? `Preferred meeting: ${meeting}` : "", form.phone ? `Phone: ${form.phone}` : ""];
  return [`${reason}`, ...bits.filter(Boolean)].join("\n");
}

function meetingStamp(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return addDaysStamp(0, date);
}

export function deskHolderName(deskId) {
  const owner = ownerRecord(deskId);
  return owner?.name || "";
}
